import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Flag,
  Gamepad2,
  Keyboard,
  Loader2,
  Maximize2,
  RotateCcw
} from 'lucide-react';
import GameEmbed from './GameEmbed';
import { NEWTON_SEQUENCE } from './newtonSequence';
import { useStoryTelemetry } from '../services/StoryTelemetry';
import './NewtonStoryPlayer.css';

const NEWTON_LEVELS = NEWTON_SEQUENCE;

const clampLevelIndex = (index) => (
  Math.max(0, Math.min(index, NEWTON_LEVELS.length - 1))
);

const isCompletionEvent = (event = {}) => {
  if (event.completed === true || event.success === true) return true;
  const eventName = String(event.event_name || event.eventType || event.type || event.name || '').toLowerCase();
  const status = String(event.status || event.phase || '').toLowerCase();
  const verb = String(event.verb || event.verb_id || '').toLowerCase();
  return (
    eventName.includes('complete') ||
    eventName.includes('finish') ||
    status === 'completed' ||
    status === 'complete' ||
    status === 'passed' ||
    verb.includes('completed') ||
    verb.includes('passed')
  );
};

const NewtonStoryPlayer = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedIds, setCompletedIds] = useState([]);
  const [loadedIds, setLoadedIds] = useState([]);
  const theaterRef = useRef(null);
  const { logStoryEvent } = useStoryTelemetry({
    storyId: 'newton',
    moduleId: 'story-newton'
  });

  const currentLevel = NEWTON_LEVELS[currentIndex];
  const completedSet = useMemo(() => new Set(completedIds), [completedIds]);
  const loadedSet = useMemo(() => new Set(loadedIds), [loadedIds]);
  const storyComplete = completedSet.size === NEWTON_LEVELS.length;

  const currentStoryEventDetails = useCallback((overrides = {}) => ({
    stage: 'unity-module',
    sceneId: currentLevel.gameId,
    targetModuleId: currentLevel.gameId,
    sequenceId: 'newton-physics-challenge',
    sequenceIndex: currentIndex,
    ...overrides
  }), [currentIndex, currentLevel.gameId]);

  const markLoaded = useCallback(({ gameId } = {}) => {
    const loadedGameId = gameId || currentLevel.gameId;
    setLoadedIds((current) => (
      current.includes(loadedGameId) ? current : [...current, loadedGameId]
    ));
    logStoryEvent('story_module_loaded', currentStoryEventDetails({
      sceneId: loadedGameId,
      targetModuleId: loadedGameId,
      status: 'loaded'
    }));
  }, [currentLevel.gameId, currentStoryEventDetails, logStoryEvent]);

  const goToLevel = useCallback((index) => {
    setCurrentIndex(clampLevelIndex(index));
  }, []);

  const completeCurrentLevel = useCallback(() => {
    logStoryEvent('story_module_complete', currentStoryEventDetails({
      completed: true,
      status: 'completed'
    }));

    if (currentIndex === NEWTON_LEVELS.length - 1) {
      logStoryEvent('story_session_complete', currentStoryEventDetails({
        completed: true,
        status: 'completed'
      }));
    }

    setCompletedIds((current) => (
      current.includes(currentLevel.gameId)
        ? current
        : [...current, currentLevel.gameId]
    ));

    if (currentIndex < NEWTON_LEVELS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, currentLevel.gameId, currentStoryEventDetails, logStoryEvent]);

  const handleUnityGameEvent = useCallback((event) => {
    if (isCompletionEvent(event)) {
      completeCurrentLevel();
    }
  }, [completeCurrentLevel]);

  const restartStory = useCallback(() => {
    logStoryEvent('story_stage_enter', {
      stage: 'unity-module',
      sceneId: NEWTON_LEVELS[0].gameId,
      targetModuleId: NEWTON_LEVELS[0].gameId,
      sequenceId: 'newton-physics-challenge',
      sequenceIndex: 0,
      status: 'restarted'
    });
    setCurrentIndex(0);
    setCompletedIds([]);
  }, [logStoryEvent]);

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
      document.exitFullscreen();
      return;
    }
    if (theaterRef.current?.requestFullscreen) {
      theaterRef.current.requestFullscreen();
    }
  }, []);

  React.useEffect(() => {
    logStoryEvent('story_stage_enter', currentStoryEventDetails({
      status: 'entered'
    }));
  }, [currentStoryEventDetails, logStoryEvent]);

  return (
    <main className="newton-story-page">
      <section className="newton-story-shell" aria-label="Newton physics story player">
        <header className="newton-story-header">
          <div>
            <Link to="/" className="newton-story-back">
              <ArrowLeft size={18} />
              <span>Back to Ping</span>
            </Link>
            <h1>Newton Physics Story</h1>
            <p>{currentLevel.title}</p>
          </div>

          <div className="newton-story-actions">
            <button className="newton-story-tool-button" type="button" onClick={restartStory}>
              <RotateCcw size={17} />
              Restart
            </button>
            <button className="newton-story-tool-button" type="button" onClick={handleFullscreen}>
              <Maximize2 size={17} />
              Fullscreen
            </button>
          </div>
        </header>

        <div className="newton-story-layout">
          <div className="newton-story-theater" ref={theaterRef}>
            <div className="newton-story-frame" aria-label="Newton story game frame">
              <GameEmbed
                key={currentLevel.gameId}
                embedded
                gameIdOverride={currentLevel.gameId}
                storyContext={{
                  storyId: 'newton',
                  sceneId: currentLevel.gameId,
                  sequenceId: 'newton-physics-challenge',
                  sequenceIndex: currentIndex
                }}
                onUnityLoaded={markLoaded}
                onUnityGameEvent={handleUnityGameEvent}
              />
            </div>

            <div className="newton-story-helpbar" aria-label="Current Newton level controls">
              <Keyboard size={18} />
              <div>
                <strong>{currentLevel.title}</strong>
                <span>{currentLevel.description}</span>
              </div>
              <button className="newton-story-primary" type="button" onClick={completeCurrentLevel}>
                {currentIndex === NEWTON_LEVELS.length - 1 ? 'Finish Story' : 'Continue to Next Level'}
              </button>
            </div>
          </div>

          <aside className="newton-story-route" aria-label="Newton story route">
            <div className="newton-story-route-top">
              <Flag size={17} aria-hidden="true" />
              <div>
                <h2>Level Route</h2>
                <p>Four games play as one story path.</p>
              </div>
            </div>

            <div className="newton-story-level-list">
              {NEWTON_LEVELS.map((level, index) => {
                const isCurrent = index === currentIndex;
                const isDone = completedSet.has(level.gameId);
                const isUnlocked = index === 0 || completedSet.has(NEWTON_LEVELS[index - 1].gameId);
                const status = isCurrent ? 'Current' : isDone ? 'Done' : isUnlocked ? 'Ready' : 'Locked';

                return (
                  <button
                    className={`newton-story-level is-${status.toLowerCase()}`}
                    data-testid={`newton-level-${level.gameId}`}
                    disabled={!isUnlocked}
                    key={level.gameId}
                    type="button"
                    onClick={() => goToLevel(index)}
                  >
                    <span className="newton-story-level-icon" aria-hidden="true">
                      {isDone && <CheckCircle2 size={16} />}
                      {!isDone && isCurrent && <Gamepad2 size={16} />}
                      {!isDone && !isCurrent && loadedSet.has(level.gameId) && <Loader2 size={16} />}
                      {!isDone && !isCurrent && !loadedSet.has(level.gameId) && <Circle size={16} />}
                    </span>
                    <span className="newton-story-level-title">{level.title}</span>
                    <span className="newton-story-level-status">{status}</span>
                  </button>
                );
              })}
            </div>

            <div className={`newton-story-finish ${storyComplete ? 'is-complete' : ''}`}>
              <CheckCircle2 size={18} />
              <span>{storyComplete ? 'Story Complete' : `${completedIds.length}/${NEWTON_LEVELS.length} complete`}</span>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default NewtonStoryPlayer;
